/**
 * Session context — holds the current `AuthUser` once `/users/me`
 * resolves, exposes `logout()` to the rest of the app. AuthGuard sets
 * the user via `setUser` after a successful `/me` (or login) call;
 * Topbar's sign-out button calls `logout()`.
 *
 * Lives outside `src/dev/` because real prod components depend on it.
 * Defaults to `null` (no session); only the guard populates it.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import type { AuthUser } from '@/apis/types'

interface SessionContextValue {
  readonly user: AuthUser | null
  readonly setUser: (user: AuthUser | null) => void
}

const SessionContext = createContext<SessionContextValue | null>(null)

interface SessionProviderProps {
  readonly children: ReactNode
  /** Override for tests — pre-seed the user. */
  readonly initialUser?: AuthUser | null
}

export function SessionProvider({ children, initialUser = null }: SessionProviderProps) {
  const [user, setUserState] = useState<AuthUser | null>(initialUser)
  const setUser = useCallback((next: AuthUser | null) => setUserState(next), [])
  const value = useMemo(() => ({ user, setUser }), [user, setUser])
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSession must be used within a SessionProvider')
  return ctx
}

/** Read-only convenience — components that only need the current user
 *  shouldn't have to destructure `setUser`. */
export function useCurrentUser(): AuthUser | null {
  return useSession().user
}
